/**
 * AI CONTEXT
 * Purpose: pure helpers for the Standout Review dashboard — project/level
 *   resolution for moments that predate those stamped fields, reaction
 *   utilities (including the null = "cleared" convention), the filter
 *   predicate + faceted option counts (same pattern as
 *   `qualityCheckHelpers.buildQualityCheckOptions`), grouping for the five
 *   stream modes, "grade the graders" teacher stats, the batched
 *   mark-as-viewed update, the like-notification copy, and the userSettings
 *   filter-shape parser. No React, no Firebase.
 *
 * Project/level resolution: a moment stamps `projectId` at save time on the
 * teacher quick-log path (`StandoutMomentPanel`); older moments and the
 * eval-authoring path infer it from the class's session schedule
 * (`resolveMomentProjectId`). Level is never stamped — it always resolves
 * through the moment's curriculum group (which ladder) and the student's
 * CURRENT level on that ladder (`getMomentLevel`), matching how
 * `qualityCheckHelpers` reads a student's level per ladder.
 */
import dayjs from "dayjs"
import {
  chunk,
  groupBy,
  isPlainObject,
  maxBy,
  sortBy,
  sumBy,
  uniq,
} from "lodash"

import {
  ALL_SKILL_LEVEL_LABELS,
  type AllSkillLevels,
} from "#constants/skillLevelConstants"
import { type ClassSessionConstructor } from "#customTypes/composedTypes/classSessionTypes"
import { type ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { type CurriculumConstructor } from "#customTypes/composedTypes/curriculumTypes"
import {
  DOC_TYPES,
  type FirebaseBatchUpdate,
} from "#customTypes/composedTypes/firebaseTypes"
import { type ProjectConstructor } from "#customTypes/composedTypes/projectTypes"
import {
  type EvaluationKeywordConstructor,
  type FamilyState,
  STANDOUT_REACTION_TYPE,
  type StandoutCategory,
  type StandoutMomentConstructor,
  type StandoutReaction,
  type StandoutReactionType,
  type StandoutSource,
} from "#customTypes/composedTypes/standoutTypes"
import { type StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { type UserConstructor } from "#customTypes/composedTypes/userTypes"
import {
  type StandoutReviewFiltersSetting,
  type StandoutReviewGroupBy,
  type Uuid,
} from "#customTypes/globalTypes"
import { type IsoDate } from "#customTypes/types"
import { getCurrentSemester } from "#utils/semesterHelpers"
import {
  deriveFamilyState,
  familySharePatch,
} from "#utils/standoutMomentMigration"
import {
  getSkillLevelForClassGroup,
  type StudentSkillSource,
} from "#utils/studentSkillHelpers"

// ---------------------------------------------------------------------------
// Joins
// ---------------------------------------------------------------------------

export interface StandoutReviewJoins {
  classesById: Record<Uuid, ClassConstructor>
  curriculaById: Record<Uuid, CurriculumConstructor>
  projectsById: Record<Uuid, ProjectConstructor>
  studentsById: Record<Uuid, StudentConstructor>
  usersById: Record<Uuid, UserConstructor>
  tagsById: Record<Uuid, EvaluationKeywordConstructor>
  sessionsByClassId: Record<Uuid, ClassSessionConstructor[]>
}

/** Convenience for callers holding arrays — same shape as `qualityCheckHelpers.indexById`. */
export const indexById = <T extends { id: Uuid }>(
  docs: readonly T[]
): Record<Uuid, T> =>
  docs.reduce<Record<Uuid, T>>((index, doc) => {
    index[doc.id] = doc
    return index
  }, {})

export const groupSessionsByClassId = (
  sessions: readonly ClassSessionConstructor[]
): Record<Uuid, ClassSessionConstructor[]> => groupBy(sessions, "classId")

// ---------------------------------------------------------------------------
// Project resolution
// ---------------------------------------------------------------------------

/**
 * `projectId` when the moment stamped it (teacher quick-log path, current
 * build); otherwise the project of the LATEST session of the moment's class
 * that started on or before the moment — sessions carry real dates
 * (including skip-day shifts), so this is exact, not a schedule guess. No
 * classId, or no sessions loaded for it, → null.
 */
export const resolveMomentProjectId = (
  moment: Pick<
    StandoutMomentConstructor,
    "projectId" | "classId" | "createdAt"
  >,
  sessionsByClassId: Record<Uuid, ClassSessionConstructor[]>
): Uuid | null => {
  if (moment.projectId) return moment.projectId
  if (!moment.classId) return null

  const sessions = sessionsByClassId[moment.classId]
  if (!sessions?.length) return null

  const createdAtMs = dayjs(moment.createdAt).valueOf()
  const eligible = sessions.filter(
    (session) => dayjs(session.eventStart).valueOf() <= createdAtMs
  )
  if (eligible.length === 0) return null

  const latest = maxBy(eligible, (session) =>
    dayjs(session.eventStart).valueOf()
  )
  return latest?.projectId ?? null
}

// ---------------------------------------------------------------------------
// Level resolution
// ---------------------------------------------------------------------------

/**
 * The moment's curriculum GROUP picks the ladder (CORE, PAINTING and
 * PORTFOLIO all read the core ladder — `CLASS_GROUP_TO_SKILL_TYPE`); the
 * level itself is the student's own CURRENT level on that ladder, resolved
 * from `skillHistory` for this semester rather than read off the stale flat
 * field. Missing curriculum or student → null (nothing to resolve the
 * ladder through).
 */
export const getMomentLevel = (
  moment: Pick<StandoutMomentConstructor, "curriculumId" | "studentId">,
  studentsById: Record<Uuid, StudentSkillSource>,
  curriculaById: Record<Uuid, Pick<CurriculumConstructor, "group">>
): AllSkillLevels | null => {
  const curriculum = curriculaById[moment.curriculumId]
  if (!curriculum) return null
  const student = studentsById[moment.studentId]
  if (!student) return null

  return getSkillLevelForClassGroup(student, curriculum.group) || null
}

export const describeMomentLevel = (level: AllSkillLevels): string =>
  `${ALL_SKILL_LEVEL_LABELS[level]} (${level})`

// ---------------------------------------------------------------------------
// Reactions
// ---------------------------------------------------------------------------

/**
 * Non-null reaction entries only — a `null` value means the reviewer
 * cleared their reaction (written instead of `deleteField` so it survives
 * `cleanFormData`; see `StandoutReaction` in standoutTypes.ts) and must never
 * be treated as an active reaction.
 */
export const getMomentReactions = (
  moment: Pick<StandoutMomentConstructor, "reactions">
): Array<{ userId: Uuid; reaction: StandoutReaction }> =>
  Object.entries(moment.reactions ?? {})
    .filter((entry): entry is [Uuid, StandoutReaction] => entry[1] != null)
    .map(([userId, reaction]) => ({ userId, reaction }))

export const getLikes = (
  moment: Pick<StandoutMomentConstructor, "reactions">
): Array<{ userId: Uuid; reaction: StandoutReaction }> =>
  getMomentReactions(moment).filter(
    (entry) => entry.reaction.type === STANDOUT_REACTION_TYPE.LIKE
  )

export const getNeedsWork = (
  moment: Pick<StandoutMomentConstructor, "reactions">
): Array<{ userId: Uuid; reaction: StandoutReaction }> =>
  getMomentReactions(moment).filter(
    (entry) => entry.reaction.type === STANDOUT_REACTION_TYPE.NEEDS_WORK
  )

export const isUnviewed = (
  moment: Pick<StandoutMomentConstructor, "viewedAt">
): boolean => !moment.viewedAt

/** The CURRENT user's own reaction (or null), for the toggle buttons' pressed state. */
export const getUserReaction = (
  moment: Pick<StandoutMomentConstructor, "reactions">,
  userId: Uuid
): StandoutReactionType | null => moment.reactions?.[userId]?.type ?? null

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

export type StandoutReviewMomentFilters = Pick<
  StandoutReviewFiltersSetting,
  "teacherId" | "classId" | "category" | "curriculumId"
>

export const EMPTY_STANDOUT_REVIEW_MOMENT_FILTERS: StandoutReviewMomentFilters =
  {
    teacherId: null,
    classId: null,
    category: null,
    curriculumId: null,
  }

/**
 * A teacher filter on a Story Time matches the class's teachers, the same
 * rule as a class scan. `createdBy` is often a TA, and that would hide the
 * recording when the class teacher is selected.
 */
export const storyTimeMatchesTeacher = (
  storyTime: { classId?: string | null },
  teacherId: string | null,
  classesById: Record<
    string,
    | {
        teacherIds?: readonly string[] | null
        defaultTeacherId?: string | null
      }
    | undefined
  >
): boolean => {
  if (teacherId === null) return true
  const classObj = classesById[storyTime.classId ?? ""]
  return (
    (classObj?.teacherIds ?? []).includes(teacherId) ||
    classObj?.defaultTeacherId === teacherId
  )
}

/**
 * `teacherId` matches the AUTHOR (`createdBy`) — moments are reviewed by
 * who wrote them, not who they're about. `curriculumId` matches the
 * curriculum stamped on the moment.
 */
export const momentMatchesFilters = (
  moment: StandoutMomentConstructor,
  filters: StandoutReviewMomentFilters
): boolean => {
  if (filters.teacherId !== null && moment.createdBy !== filters.teacherId) {
    return false
  }
  if (filters.classId !== null && moment.classId !== filters.classId) {
    return false
  }
  if (filters.category !== null && moment.category !== filters.category) {
    return false
  }
  if (
    filters.curriculumId !== null &&
    moment.curriculumId !== filters.curriculumId
  ) {
    return false
  }
  return true
}

// ---------------------------------------------------------------------------
// Faceted options
// ---------------------------------------------------------------------------

export interface StandoutReviewOption<V extends string> {
  value: V
  label: string
  count: number
}

export interface StandoutReviewFilterOptions {
  teachers: StandoutReviewOption<Uuid>[]
  classes: StandoutReviewOption<Uuid>[]
  curricula: StandoutReviewOption<Uuid>[]
  /** Fixed 3-value enum, never hidden at zero — mirrors QC's `scopeCounts`. */
  categoryCounts: Record<StandoutCategory, number>
}

const countWhere = <T>(items: readonly T[], predicate: (item: T) => boolean) =>
  items.reduce((n, item) => (predicate(item) ? n + 1 : n), 0)

/**
 * Faceted like Quality Check's session list: each axis's options/counts apply
 * every OTHER active filter but never itself, and a currently-selected value
 * stays pinned even at a zero count (undoable, and keeps the Select showing
 * its label instead of a raw id). Category is a small closed enum, so —
 * like QC's scope radio — it always lists every value rather than hiding
 * options that would show zero.
 */
export const buildStandoutReviewOptions = (
  moments: readonly StandoutMomentConstructor[],
  joins: StandoutReviewJoins,
  filters: StandoutReviewMomentFilters
): StandoutReviewFilterOptions => {
  const exceptTeacher = moments.filter((moment) =>
    momentMatchesFilters(moment, { ...filters, teacherId: null })
  )
  const exceptClass = moments.filter((moment) =>
    momentMatchesFilters(moment, { ...filters, classId: null })
  )
  const exceptCurriculum = moments.filter((moment) =>
    momentMatchesFilters(moment, { ...filters, curriculumId: null })
  )
  const exceptCategory = moments.filter((moment) =>
    momentMatchesFilters(moment, { ...filters, category: null })
  )

  const teacherIds = new Set<Uuid>()
  for (const moment of exceptTeacher) teacherIds.add(moment.createdBy)
  if (filters.teacherId !== null) teacherIds.add(filters.teacherId)
  const teachers = sortBy(
    [...teacherIds].map((teacherId) => ({
      value: teacherId,
      label: joins.usersById[teacherId]?.name ?? "Unknown teacher",
      count: countWhere(
        exceptTeacher,
        (moment) => moment.createdBy === teacherId
      ),
    })),
    (option) => option.label.toLowerCase(),
    (option) => option.value
  )

  const classIds = new Set<Uuid>()
  for (const moment of exceptClass) {
    if (moment.classId) classIds.add(moment.classId)
  }
  if (filters.classId !== null) classIds.add(filters.classId)
  const classes = sortBy(
    [...classIds].map((classId) => ({
      value: classId,
      label: joins.classesById[classId]?.name ?? "Unknown class",
      count: countWhere(exceptClass, (moment) => moment.classId === classId),
    })),
    (option) => option.label.toLowerCase(),
    (option) => option.value
  )

  const curriculumIds = new Set<Uuid>()
  for (const moment of exceptCurriculum) {
    if (moment.curriculumId) curriculumIds.add(moment.curriculumId)
  }
  if (filters.curriculumId !== null) curriculumIds.add(filters.curriculumId)
  const curricula = sortBy(
    [...curriculumIds].map((curriculumId) => ({
      value: curriculumId,
      label: joins.curriculaById[curriculumId]?.name ?? curriculumId,
      count: countWhere(
        exceptCurriculum,
        (moment) => moment.curriculumId === curriculumId
      ),
    })),
    (option) => option.label.toLowerCase(),
    (option) => option.value
  )

  const categoryCounts: Record<StandoutCategory, number> = {
    thumbsUp: countWhere(
      exceptCategory,
      (moment) => moment.category === "thumbsUp"
    ),
    needsWork: countWhere(
      exceptCategory,
      (moment) => moment.category === "needsWork"
    ),
    secret: countWhere(
      exceptCategory,
      (moment) => moment.category === "secret"
    ),
  }

  return { teachers, classes, curricula, categoryCounts }
}

// ---------------------------------------------------------------------------
// Student search
// ---------------------------------------------------------------------------

/**
 * Case-insensitive substring match on the student's name (raw id as the
 * fallback the card also displays). Applied BEFORE the facets, like the
 * semester scope — searching narrows every option count.
 */
export const momentMatchesStudentSearch = (
  moment: Pick<StandoutMomentConstructor, "studentId">,
  searchTerm: string,
  studentsById: Record<Uuid, StudentConstructor>
): boolean => {
  const normalized = searchTerm.trim().toLowerCase()
  if (!normalized) return true
  const name = studentsById[moment.studentId]?.name ?? moment.studentId
  return name.toLowerCase().includes(normalized)
}

// ---------------------------------------------------------------------------
// Display sort order
// ---------------------------------------------------------------------------

/**
 * Newest moment first inside a stream group. The same timestamp ties on id
 * so a late-arriving join cannot reshuffle the cards.
 */
export const sortMomentsForDisplay = (
  moments: readonly StandoutMomentConstructor[]
): StandoutMomentConstructor[] =>
  sortBy(moments, [
    (moment) => -dayjs(moment.createdAt).valueOf(),
    (moment) => moment.id,
  ])

const newestCreatedAt = (
  moments: readonly StandoutMomentConstructor[]
): number =>
  moments.reduce((latest, moment) => {
    const at = dayjs(moment.createdAt).valueOf()
    return at > latest ? at : latest
  }, Number.NEGATIVE_INFINITY)

// ---------------------------------------------------------------------------
// Grouping
// ---------------------------------------------------------------------------

export interface StandoutReviewSubgroup {
  key: string
  label: string
  moments: StandoutMomentConstructor[]
}

export interface StandoutReviewGroup {
  key: string
  label: string
  /**
   * False when the label is a fallback ("Unknown class", "No … record")
   * because the joined doc hasn't arrived or is gone. Unresolved groups sort
   * last so that a label arriving late cannot re-order the groups above it —
   * label-only sorting made the stream shuffle as `useSomeDocs` filled in.
   */
  resolved: boolean
  moments: StandoutMomentConstructor[]
  /** Teacher subgroups — class mode only (owner ask: cards inside a class
   * group shouldn't need to repeat the teacher's name). */
  subgroups?: StandoutReviewSubgroup[]
}

const NO_GROUP_KEY = "__unresolved__"

const NO_GROUP_LABEL: Record<StandoutReviewGroupBy, string> = {
  class: "No class record",
  teacher: "No teacher record",
  student: "No student record",
  level: "No level record",
  day: "No date record",
}

const DAY_LABEL_FORMAT = "ddd, MMM D YYYY"

const resolveGroupKey = (
  moment: StandoutMomentConstructor,
  groupBy: StandoutReviewGroupBy,
  joins: StandoutReviewJoins
): { key: string; label: string; resolved: boolean } => {
  switch (groupBy) {
    case "class": {
      const classId = moment.classId
      if (!classId) {
        return {
          key: NO_GROUP_KEY,
          label: NO_GROUP_LABEL.class,
          resolved: false,
        }
      }
      // A classId whose doc hasn't resolved (still loading, or deleted) keeps
      // its OWN per-id group — merging those into the no-record bucket made
      // the whole stream flash into one group while `useSomeDocs` filled in,
      // and permanently mixed deleted-class moments with class-less ones
      // (Bugbot, PR #109). Teacher/student modes already key per id.
      const classObj = joins.classesById[classId]
      return {
        key: classId,
        label: classObj?.name ?? "Unknown class",
        resolved: !!classObj,
      }
    }
    case "teacher": {
      const teacherId = moment.createdBy
      if (!teacherId)
        return {
          key: NO_GROUP_KEY,
          label: NO_GROUP_LABEL.teacher,
          resolved: false,
        }
      const teacher = joins.usersById[teacherId]
      return {
        key: teacherId,
        label: teacher?.name ?? "Unknown teacher",
        resolved: !!teacher,
      }
    }
    case "student": {
      const studentId = moment.studentId
      if (!studentId)
        return {
          key: NO_GROUP_KEY,
          label: NO_GROUP_LABEL.student,
          resolved: false,
        }
      const student = joins.studentsById[studentId]
      return {
        key: studentId,
        label: student?.name ?? "Unknown student",
        resolved: !!student,
      }
    }
    case "level": {
      const level = getMomentLevel(
        moment,
        joins.studentsById,
        joins.curriculaById
      )
      if (!level)
        return {
          key: NO_GROUP_KEY,
          label: NO_GROUP_LABEL.level,
          resolved: false,
        }
      return { key: level, label: describeMomentLevel(level), resolved: true }
    }
    case "day": {
      const key = dayjs(moment.createdAt).format("YYYY-MM-DD")
      const label = dayjs(moment.createdAt).format(DAY_LABEL_FORMAT)
      return { key, label, resolved: true }
    }
    default:
      return { key: NO_GROUP_KEY, label: "Other", resolved: false }
  }
}

/**
 * Class mode's teacher subgroups — one per `createdBy`, sorted by teacher
 * label. `createdBy` is a required field, so unlike the top-level "teacher"
 * group-by this never needs a "No teacher record" bucket, just the same
 * "Unknown teacher" fallback the faceted options use for an unloaded user.
 */
const buildTeacherSubgroups = (
  moments: readonly StandoutMomentConstructor[],
  joins: StandoutReviewJoins
): StandoutReviewSubgroup[] => {
  const byTeacher = groupBy(moments, "createdBy")
  const subgroups = Object.entries(byTeacher).map(
    ([teacherId, teacherMoments]) => ({
      key: teacherId,
      label: joins.usersById[teacherId]?.name ?? "Unknown teacher",
      moments: sortMomentsForDisplay(teacherMoments),
    })
  )
  return sortBy(subgroups, [
    (subgroup) => subgroup.label.toLowerCase(),
    (subgroup) => subgroup.key,
  ])
}

/**
 * Ordered groups, newest moment first, and `sortMomentsForDisplay`-ordered
 * WITHIN each group. Day groups are one calendar day, so that order is
 * newest day first. A moment that can't resolve its axis (e.g. no classId)
 * lands in an explicit "No … record" group instead of being dropped. Class
 * mode additionally nests each group (including the "No class record"
 * bucket) into per-teacher subgroups — see `buildTeacherSubgroups`.
 */
export const groupMoments = (
  moments: readonly StandoutMomentConstructor[],
  groupBy: StandoutReviewGroupBy,
  joins: StandoutReviewJoins
): StandoutReviewGroup[] => {
  const groups = new Map<string, StandoutReviewGroup>()

  for (const moment of moments) {
    const { key, label, resolved } = resolveGroupKey(moment, groupBy, joins)
    let group = groups.get(key)
    if (!group) {
      group = { key, label, resolved, moments: [] }
      groups.set(key, group)
    }
    group.moments.push(moment)
  }

  for (const group of groups.values()) {
    group.moments = sortMomentsForDisplay(group.moments)
    if (groupBy === "class") {
      group.subgroups = buildTeacherSubgroups(group.moments, joins)
    }
  }

  const groupList = [...groups.values()]
  // Newest moment first, then key. Day groups come out newest day first
  // because each group's newest moment is on that day. The key tie-break
  // keeps the order deterministic while joins are still arriving: sorting
  // on the label alone left every not-yet-named group tied, so one doc
  // landing could re-sort the whole stream under the user's cursor.
  return sortBy(groupList, [
    (group) => -newestCreatedAt(group.moments),
    (group) => group.key,
  ])
}

/** Per-group header counts: total, never-viewed, liked, needs-work. */
export const countGroupStates = (
  moments: readonly StandoutMomentConstructor[]
): { total: number; unviewed: number; liked: number; needsWork: number } => ({
  total: moments.length,
  unviewed: countWhere(moments, isUnviewed),
  liked: countWhere(moments, (moment) => getLikes(moment).length > 0),
  needsWork: countWhere(moments, (moment) => getNeedsWork(moment).length > 0),
})

export const countUnviewed = (
  moments: readonly StandoutMomentConstructor[]
): number => countWhere(moments, isUnviewed)

export const countDistinctTeachers = (
  moments: readonly StandoutMomentConstructor[]
): number => uniq(moments.map((moment) => moment.createdBy)).length

// ---------------------------------------------------------------------------
// Family approve selection
// ---------------------------------------------------------------------------

/**
 * A moment the family-approve checkbox may consider. Narrow on purpose so
 * the selection rule can be tested without a full standout doc.
 */
export interface FamilyApproveMoment {
  id: string
  studentId: string
  isShared?: boolean
  familyState?: FamilyState
  source?: StandoutSource
  classSessionId?: string
}

/** `pending` is checkable. `auto` is scanner or already approved. `sent` is folded. `undeliverable` has no class session. */
export type FamilyReviewBucket = "pending" | "auto" | "sent" | "undeliverable"

/**
 * Scanner rows are auto even when `familyState` was left pending and
 * `isShared` was never stored. A teacher unshare writes `pending` and
 * `isShared: false`; that row is pending again so the feed checkbox can
 * select it. A scanner row that has already been sent stays `sent` so it
 * folds with the other sent rows. Approved-on-arrival (including legacy
 * `isShared` with no `familyState`) is auto. A pending row with no
 * `classSessionId` is undeliverable: the sweep cannot send it.
 */
export const familyReviewBucket = (
  moment: Pick<
    FamilyApproveMoment,
    "isShared" | "familyState" | "source" | "classSessionId"
  >
): FamilyReviewBucket => {
  const state = deriveFamilyState(moment)
  if (state === "sent") return "sent"
  const unsharedScanner =
    moment.source === "scanner" &&
    state === "pending" &&
    moment.isShared === false
  if (
    !unsharedScanner &&
    (moment.source === "scanner" || state === "approved")
  ) {
    return "auto"
  }
  if (!moment.classSessionId) return "undeliverable"
  return "pending"
}

/**
 * The visible word for the auto bucket. Nothing is stored as "auto".
 * A scanner note that was not unshared arrived already included. Any
 * other auto row is already approved.
 */
export const familyReviewAutoLabel = (
  moment: Pick<FamilyApproveMoment, "source" | "familyState" | "isShared">
): "Scanner" | "Approved" => {
  if (moment.source === "scanner" && deriveFamilyState(moment) !== "approved") {
    return "Scanner"
  }
  return "Approved"
}

/** "1 scanner · 2 approved", or null when the group has no auto rows. */
export const familyReviewAutoSummary = (
  moments: readonly Pick<
    FamilyApproveMoment,
    "isShared" | "familyState" | "source" | "classSessionId"
  >[]
): string | null => {
  let scanner = 0
  let approved = 0
  for (const moment of moments) {
    if (familyReviewBucket(moment) !== "auto") continue
    if (familyReviewAutoLabel(moment) === "Scanner") scanner += 1
    else approved += 1
  }
  const parts = [
    scanner > 0 ? `${scanner} scanner` : null,
    approved > 0 ? `${approved} approved` : null,
  ].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(" · ") : null
}

export const countFamilyReviewBuckets = (
  moments: readonly Pick<
    FamilyApproveMoment,
    "isShared" | "familyState" | "source" | "classSessionId"
  >[]
): { pending: number; auto: number; sent: number; undeliverable: number } => {
  const counts = { pending: 0, auto: 0, sent: 0, undeliverable: 0 }
  for (const moment of moments) {
    counts[familyReviewBucket(moment)] += 1
  }
  return counts
}

/**
 * Ids the student header checkbox may select.
 *
 * `visibleIds` is the caller's rendered set (the current filter, not folded
 * sent rows, not another panel's rows). Membership is by id. A prefix slice
 * of `moments` is wrong: hidden and out-of-range rows often sit earlier in
 * the array than the rows on screen. Auto-approved, sent, and any other
 * student's rows are excluded even when they are visible. A scanner row the
 * teacher unshared is pending, so it is included.
 */
export const selectablePendingIdsForStudent = (
  moments: readonly FamilyApproveMoment[],
  studentId: string,
  visibleIds: ReadonlySet<string>
): string[] =>
  moments
    .filter(
      (moment) =>
        moment.studentId === studentId &&
        visibleIds.has(moment.id) &&
        familyReviewBucket(moment) === "pending"
    )
    .map((moment) => moment.id)

/**
 * `familyState` and `isShared` move together. See `familySharePatch` for
 * why a contentSweep stamp survives unshare and a later re-approve.
 */
export const familyStateWrite = (
  state: FamilyState,
  userId: Uuid,
  nowIso: IsoDate,
  existingSharedBy?: string | null
) => familySharePatch(state, userId, nowIso, existingSharedBy)

// ---------------------------------------------------------------------------
// Teacher stats ("grade the graders")
// ---------------------------------------------------------------------------

export interface StandoutTeacherStatsRow {
  teacherId: Uuid
  teacherName: string
  total: number
  thumbsUp: number
  needsWork: number
  secret: number
  /** Percent (0–100, rounded) of this teacher's moments carrying a note. */
  withNotePct: number
  /** Count of this teacher's moments shared with parents (`isShared`). */
  sharedCount: number
  distinctStudents: number
  /**
   * Percent (0–100, rounded) of the teacher's CURRENT-semester roster with
   * at least one moment from this teacher, or null when the teacher has no
   * roster at all (a "no data" state, not a real 0%).
   */
  studentsWithFeedbackPct: number | null
  /** Distinct students across the teacher's semester class rosters. */
  rosterSize: number
  /** Likes/needs-work RECEIVED — reactions other reviewers left on this teacher's moments. */
  likesReceived: number
  needsWorkReceived: number
  lastActivity: IsoDate
}

/**
 * Who taught the class: `teacherIds` (aggregated from sessions) OR
 * `defaultTeacherId` (set before any session recorded a teacher) — a class
 * counts toward a teacher's roster either way.
 */
const classHasTeacher = (
  classObj: Pick<ClassConstructor, "teacherIds" | "defaultTeacherId">,
  teacherId: Uuid
): boolean =>
  (classObj.teacherIds ?? []).includes(teacherId) ||
  classObj.defaultTeacherId === teacherId

/** The distinct students across every semester class this teacher is on. */
export const getTeacherRosterStudentIds = (
  teacherId: Uuid,
  semesterClasses: readonly ClassConstructor[]
): Uuid[] =>
  uniq(
    semesterClasses
      .filter((classObj) => classHasTeacher(classObj, teacherId))
      .flatMap((classObj) => classObj.studentIds ?? [])
  )

/**
 * One row per author (`createdBy`), computed from ALL of the semester's
 * moments — this table is about teachers overall, not whatever slice the
 * stream filters currently show (owner ask: comparing writers fairly means
 * comparing them on the same denominator). `semesterClasses` (the same
 * semester the moments are scoped to) supplies each teacher's roster for
 * the feedback-coverage percentage.
 */
export const computeTeacherStats = (
  moments: readonly StandoutMomentConstructor[],
  joins: Pick<StandoutReviewJoins, "usersById">,
  semesterClasses: readonly ClassConstructor[]
): StandoutTeacherStatsRow[] => {
  const byTeacher = groupBy(moments, "createdBy")

  return Object.entries(byTeacher).map(([teacherId, teacherMoments]) => {
    const total = teacherMoments.length
    const withNoteCount = countWhere(teacherMoments, (moment) => !!moment.note)
    const latest = maxBy(teacherMoments, (moment) =>
      dayjs(moment.createdAt).valueOf()
    )

    const rosterStudentIds = getTeacherRosterStudentIds(
      teacherId,
      semesterClasses
    )
    const rosterSize = rosterStudentIds.length
    const studentsWithFeedback = countWhere(rosterStudentIds, (studentId) =>
      teacherMoments.some((moment) => moment.studentId === studentId)
    )

    return {
      teacherId,
      teacherName: joins.usersById[teacherId]?.name ?? "Unknown teacher",
      total,
      thumbsUp: countWhere(
        teacherMoments,
        (moment) => moment.category === "thumbsUp"
      ),
      needsWork: countWhere(
        teacherMoments,
        (moment) => moment.category === "needsWork"
      ),
      secret: countWhere(
        teacherMoments,
        (moment) => moment.category === "secret"
      ),
      withNotePct: total > 0 ? Math.round((withNoteCount / total) * 100) : 0,
      sharedCount: countWhere(
        teacherMoments,
        (moment) => moment.isShared === true
      ),
      distinctStudents: uniq(teacherMoments.map((moment) => moment.studentId))
        .length,
      studentsWithFeedbackPct:
        rosterSize > 0
          ? Math.round((studentsWithFeedback / rosterSize) * 100)
          : null,
      rosterSize,
      likesReceived: sumBy(teacherMoments, (moment) => getLikes(moment).length),
      needsWorkReceived: sumBy(
        teacherMoments,
        (moment) => getNeedsWork(moment).length
      ),
      lastActivity: latest?.createdAt ?? "",
    }
  })
}

// ---------------------------------------------------------------------------
// Per-teacher student distribution (moments-per-student histogram)
// ---------------------------------------------------------------------------

export interface StandoutDistributionBucket {
  bucket: string
  students: Array<{ id: Uuid; name: string }>
}

/** Fixed x-axis: 0..9 exact counts, then a 10+ overflow bucket. */
const DISTRIBUTION_BUCKET_LABELS = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10+",
] as const

/**
 * Moments-per-student histogram for one teacher's CURRENT-semester roster —
 * the "unevenness" signal the stats table's distribution chart renders.
 * Bucket 0 is the deliberate point: roster students this teacher has never
 * written a moment for. Only roster students are counted (a moment about a
 * non-roster student, e.g. a tryout, isn't part of "how evenly did this
 * teacher cover their roster"). Names resolve through `studentsById`, which
 * the view only loads for students referenced by loaded moments — a roster
 * student with zero moments may not be loaded, so falls back to their id
 * (deliberately not backed by a whole-roster subscription; see AI CONTEXT).
 */
export const buildTeacherStudentDistribution = (
  teacherId: Uuid,
  moments: readonly StandoutMomentConstructor[],
  semesterClasses: readonly ClassConstructor[],
  studentsById: Record<Uuid, Pick<StudentConstructor, "name">>
): StandoutDistributionBucket[] => {
  const rosterStudentIds = getTeacherRosterStudentIds(
    teacherId,
    semesterClasses
  )

  const buckets: StandoutDistributionBucket[] = DISTRIBUTION_BUCKET_LABELS.map(
    (label) => ({ bucket: label, students: [] })
  )

  for (const studentId of rosterStudentIds) {
    const momentCount = countWhere(
      moments,
      (moment) =>
        moment.createdBy === teacherId && moment.studentId === studentId
    )
    const bucketIndex = Math.min(
      momentCount,
      DISTRIBUTION_BUCKET_LABELS.length - 1
    )
    buckets[bucketIndex].students.push({
      id: studentId,
      name: studentsById[studentId]?.name ?? studentId,
    })
  }

  return buckets.map((bucket) => ({
    ...bucket,
    students: sortBy(bucket.students, (student) => student.name.toLowerCase()),
  }))
}

// ---------------------------------------------------------------------------
// Mark-as-viewed batch write
// ---------------------------------------------------------------------------

/** Firestore batch write limit is 500; 400 leaves headroom for other writes sharing a batch. */
const MARK_VIEWED_CHUNK_SIZE = 400

export interface MarkViewedChunk {
  ids: Uuid[]
  update: FirebaseBatchUpdate
}

/**
 * Only unviewed moments are touched (already-viewed moments keep their
 * original `viewedAt`/`viewedBy`). Split into ≤400-id chunks so a big
 * "mark all visible" never risks the 500-write Firestore batch limit.
 */
export const buildMarkViewedUpdate = (
  moments: readonly StandoutMomentConstructor[],
  userId: Uuid,
  nowIso: IsoDate
): MarkViewedChunk[] => {
  const unviewed = moments.filter(isUnviewed)
  if (unviewed.length === 0) return []

  return chunk(unviewed, MARK_VIEWED_CHUNK_SIZE).map((group) => ({
    ids: group.map((moment) => moment.id),
    update: {
      [DOC_TYPES.STANDOUT_MOMENTS]: group.reduce<
        Record<Uuid, Partial<StandoutMomentConstructor>>
      >((updates, moment) => {
        updates[moment.id] = { viewedAt: nowIso, viewedBy: userId }
        return updates
      }, {}),
    },
  }))
}

// ---------------------------------------------------------------------------
// Like-notification content
// ---------------------------------------------------------------------------

/** Plain text (notifications render as plain text, not markdown). */
export const buildLikeNotificationContent = (
  moment: Pick<StandoutMomentConstructor, "tagIds" | "note" | "generatedText">,
  likerName: string,
  studentName: string,
  tagsById: Record<Uuid, EvaluationKeywordConstructor>
): { header: string; message: string } => {
  const header = `${likerName} liked your standout moment`

  const tagTexts = (moment.tagIds ?? []).map(
    (tagId) => tagsById[tagId]?.text ?? tagId
  )
  const writtenText = moment.note || moment.generatedText

  const parts = [`About ${studentName}.`]
  if (tagTexts.length > 0) parts.push(`Tags: ${tagTexts.join(", ")}.`)
  if (writtenText) parts.push(writtenText)

  return { header, message: parts.join(" ") }
}

// ---------------------------------------------------------------------------
// Display label constants
// ---------------------------------------------------------------------------

export const STANDOUT_CATEGORY_LABELS: Record<StandoutCategory, string> = {
  thumbsUp: "Thumbs Up",
  needsWork: "Needs Work",
  secret: "Secret",
}

export const STANDOUT_REVIEW_GROUP_BY_LABELS: Record<
  StandoutReviewGroupBy,
  string
> = {
  class: "Class",
  teacher: "Teacher",
  student: "Student",
  day: "Day",
  level: "Level",
}

// ---------------------------------------------------------------------------
// Persisted filters (userSettings.standoutReviewFilters)
// ---------------------------------------------------------------------------

export const DEFAULT_STANDOUT_REVIEW_FILTERS_SETTING: StandoutReviewFiltersSetting =
  {
    semester: getCurrentSemester(),
    groupBy: "student",
    teacherId: null,
    classId: null,
    category: null,
    curriculumId: null,
    hideFinished: true,
    hideFinishedClasses: false,
  }

const STANDOUT_REVIEW_GROUP_BY_VALUES: readonly StandoutReviewGroupBy[] = [
  "class",
  "teacher",
  "student",
  "day",
  "level",
]
const isStandoutReviewGroupBy = (
  value: unknown
): value is StandoutReviewGroupBy =>
  typeof value === "string" &&
  (STANDOUT_REVIEW_GROUP_BY_VALUES as readonly string[]).includes(value)

const STANDOUT_CATEGORY_VALUES: readonly StandoutCategory[] = [
  "thumbsUp",
  "needsWork",
  "secret",
]
const isStandoutCategory = (value: unknown): value is StandoutCategory =>
  typeof value === "string" &&
  (STANDOUT_CATEGORY_VALUES as readonly string[]).includes(value)

/**
 * A stored value with a missing/foreign shape is ordinary input (an older
 * build wrote it, or nothing did), so every field falls back independently —
 * same posture as `parseQualityCheckFiltersSetting`. A stored `reaction` or
 * `level` from the old radio and skill-level filter is ignored. The semester
 * default is the CURRENT semester (never "All"): this is the value the
 * moments subscription itself is scoped by, not a display filter.
 */
export const parseStandoutReviewFiltersSetting = (
  raw: unknown
): StandoutReviewFiltersSetting => {
  if (!isPlainObject(raw)) return DEFAULT_STANDOUT_REVIEW_FILTERS_SETTING
  const candidate = raw as Partial<
    Record<keyof StandoutReviewFiltersSetting, unknown>
  >

  return {
    semester:
      typeof candidate.semester === "string" && candidate.semester
        ? candidate.semester
        : getCurrentSemester(),
    groupBy: isStandoutReviewGroupBy(candidate.groupBy)
      ? candidate.groupBy
      : "student",
    teacherId:
      typeof candidate.teacherId === "string" && candidate.teacherId
        ? candidate.teacherId
        : null,
    classId:
      typeof candidate.classId === "string" && candidate.classId
        ? candidate.classId
        : null,
    category: isStandoutCategory(candidate.category)
      ? candidate.category
      : null,
    curriculumId:
      typeof candidate.curriculumId === "string" && candidate.curriculumId
        ? candidate.curriculumId
        : null,
    hideFinished: candidate.hideFinished === false ? false : true,
    hideFinishedClasses: candidate.hideFinishedClasses === true,
  }
}
